import React from "react";
import { Compass, Sparkles, Handshake, ShieldCheck } from "lucide-react";
import { IMG } from "@/lib/assets";
import { FoundersStrip } from "@/components/FoundersStrip";

const pillars = [
  {
    icon: Handshake,
    title: "A team behind you, founders overseeing",
    body: "Our team works with you day-to-day. Our co-founders oversee every project.",
  },
  {
    icon: Compass,
    title: "Curated, not templated",
    body: "Built around your tools, your pace, and your goals.",
  },
  {
    icon: Sparkles,
    title: "Collaborative by default",
    body: "We build it together, so your team grows with it.",
  },
  {
    icon: ShieldCheck,
    title: "AI that grows with you",
    body: "We keep improving your AI as you grow, with regular check-ins, quarterly reviews, and a direct line whenever you need us.",
  },
];

export default function Why() {
  return (
    <section id="why" className="relative py-14 sm:py-20">
      <div className="max-w-[1240px] mx-auto px-5 sm:px-8">
        <div className="relative rounded-[28px] overflow-hidden bg-[var(--night)] text-white p-7 sm:p-10">
          {/* bg layers */}
          <div className="absolute inset-0 bg-grid opacity-[0.05]" />
          <div className="absolute -top-32 -right-32 w-[520px] h-[520px] orb opacity-70" />
          <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] orb-warm opacity-45" />

          <div className="relative grid lg:grid-cols-12 gap-8 items-start">
            {/* Portrait — both co-founders, editorial two-shot */}
            <div className="lg:col-span-4 order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.5)] aspect-[928/860] max-w-[340px] mx-auto lg:mx-0">
                <div className="portrait-frame absolute inset-0">
                  <img
                    data-testid="why-cofounders-img"
                    style={{ transform: "none", objectPosition: "center top" }}
                    src={IMG.cofounders}
                    alt="Ramya Rajakumar and Munique Antunes — Co-Founders of Duaura"
                    loading="lazy"
                  />
                </div>
                <div
                  className="absolute inset-0 pointer-events-none mix-blend-soft-light"
                  style={{
                    background:
                      "linear-gradient(160deg, rgba(214,180,138,0.28) 0%, transparent 45%, rgba(69,102,199,0.22) 100%)",
                  }}
                />
                <div className="absolute top-4 left-4">
                  <span className="glass rounded-full px-3 py-1 font-mono text-[10px] tracking-[0.22em] uppercase text-[var(--ink)]">
                    The Duaura Team
                  </span>
                </div>
              </div>

            </div>

            {/* Copy */}
            <div className="lg:col-span-8 order-1 lg:order-2">
              <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--camel-soft)]/85">
                / Why Duaura
              </span>
              <h2 className="font-serif font-medium text-[34px] sm:text-[44px] leading-[1.05] mt-4 text-white">
                Not a vendor.{" "}
                <span className="italic font-italic-serif text-white/70 ml-2">
                  A collaborator.
                </span>
              </h2>


              {/* Pillars */}
              <div className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-5 pt-6 border-t border-white/10">
                {pillars.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <div
                      key={i}
                      data-testid={`why-pillar-${i}`}
                      className="flex gap-4"
                    >
                      <div className="shrink-0 w-10 h-10 rounded-xl bg-white/[0.04] border border-white/10 flex items-center justify-center">
                        <Icon
                          className="w-[17px] h-[17px] text-[var(--camel)]"
                          strokeWidth={1.6}
                        />
                      </div>
                      <div>
                        <div className="font-serif text-[17px] text-white leading-tight">
                          {p.title}
                        </div>
                        <p className="mt-1.5 text-[13.5px] leading-[1.55] text-white/65">
                          {p.body}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </div>
          <FoundersStrip />
        </div>
      </div>
    </section>
  );
}
